import React from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useBackstockAuditStatus } from "features/scratchers/hooks";
import { buildBackstockAuditDuePath } from "../../utils/currentInventoryStatusPath";

interface BackstockAuditDueBadgeProps {
  storeId?: string;
}

const formatAuditWindow = (totalSeconds: number | null): string | null => {
  if (!totalSeconds || totalSeconds <= 0) {
    return null;
  }

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [
    days > 0 ? `${days} ${days === 1 ? "day" : "days"}` : null,
    hours > 0 ? `${hours} ${hours === 1 ? "hour" : "hours"}` : null,
    minutes > 0 ? `${minutes} ${minutes === 1 ? "minute" : "minutes"}` : null,
    seconds > 0 ? `${seconds} ${seconds === 1 ? "second" : "seconds"}` : null,
  ].filter(Boolean);

  return parts.join(" ");
};

export default function BackstockAuditDueBadge({ storeId }: BackstockAuditDueBadgeProps) {
  const navigate = useNavigate();
  const { data } = useBackstockAuditStatus(storeId, { enabled: Boolean(storeId) });
  const dueCount = data?.overdue_count ?? 0;
  const auditWindow = formatAuditWindow(data?.backstock_audit_frequency_seconds ?? null);

  if (!storeId || !data?.backstock_audit_due || dueCount === 0) {
    return null;
  }

  const packLabel = dueCount === 1 ? "pack" : "packs";

  return (
    <Box
      component="aside"
      aria-label="Backstock scan required"
      sx={{
        alignItems: { xs: "stretch", sm: "center" },
        bgcolor: "#FFFDF8",
        border: "1px solid #E5E7EB",
        borderLeft: "4px solid #D58B20",
        borderRadius: "10px",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1.25, sm: 1.5 },
        mb: "10px",
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "#FFF3D6",
          borderRadius: "8px",
          color: "#A15C07",
          display: { xs: "none", sm: "flex" },
          flex: "0 0 auto",
          height: 40,
          justifyContent: "center",
          width: 40,
        }}
      >
        <Inventory2OutlinedIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: "#2D3748", fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>
          Backstock scan required
        </Typography>
        <Typography sx={{ color: "#667085", fontSize: 13, lineHeight: 1.45, mt: 0.15 }}>
          <Box component="span" sx={{ color: "#344054", fontWeight: 700 }}>
            {dueCount.toLocaleString()} {packLabel}
          </Box>{" "}
          {dueCount === 1 ? "has" : "have"} not been scanned during the current
          {auditWindow ? ` ${auditWindow} audit window.` : " audit window."}
        </Typography>
      </Box>
      <Button
        aria-label={`Review ${dueCount} backstock ${packLabel} due for scanning`}
        endIcon={<ArrowForwardRoundedIcon />}
        onClick={() => navigate(buildBackstockAuditDuePath(storeId))}
        size="small"
        variant="text"
        sx={{
          alignSelf: { xs: "flex-start", sm: "center" },
          color: "#256EA8",
          flex: "0 0 auto",
          fontSize: 13,
          fontWeight: 700,
          minWidth: 0,
          px: 1,
          textTransform: "none",
          "&:hover": { bgcolor: "#EDF6FC", color: "#195B8D" },
        }}
      >
        Review packs
      </Button>
    </Box>
  );
}
